import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import isNil from 'lodash/isNil';
import { PropsWithChildren } from 'react';

// When the first-party reverse proxy is configured, all PostHog traffic goes through it so
// requests survive ad blockers / tracking protection. ui_host must then keep pointing at the
// real PostHog host for toolbar and app links.
const posthogProxyHost = import.meta.env.VITE_POSTHOG_PROXY_HOST;
const posthogHostOptions =
  isNil(posthogProxyHost) || posthogProxyHost === ''
    ? { api_host: import.meta.env.VITE_POSTHOG_HOST }
    : { api_host: posthogProxyHost, ui_host: import.meta.env.VITE_POSTHOG_HOST };

/**
 * Wraps the app with PostHog's analytics provider and its error boundary.
 * The error boundary acts as a final safety net for any errors that escape
 * the inner application error boundaries, forwarding them to PostHog.
 */
export default function PostHogProviderWithErrorBoundary({ children }: PropsWithChildren) {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  // Without an API key, PostHogProvider would call posthog.init(undefined) and log a
  // "PostHog was initialized without a token" warning on every page load. Local dev
  // intentionally leaves the key unset (see .env.example), so skip the provider there.
  if (!apiKey) return <>{children}</>;

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        ...posthogHostOptions,
        capture_exceptions: true,
      }}
    >
      <PostHogErrorBoundary>{children}</PostHogErrorBoundary>
    </PostHogProvider>
  );
}
